
import {
  createMcpServer,
  deleteMcpServer,
  listMcpServers,
  type McpServer,
} from "../api/settings.js";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function serverView(server: McpServer): { id: string; name: string; meta: string } {
  const headers = [server.has_authorization ? "Authorization" : "", ...server.header_names].filter(Boolean);
  return {
    id: server.id,
    name: escapeHtml(server.name),
    meta: escapeHtml(headers.length > 0 ? `${server.url} - headers: ${headers.join(", ")}` : server.url),
  };
}

async function submitMcp(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  await createMcpServer({
    name: String(data.get("name") ?? "").trim(),
    url: String(data.get("url") ?? "").trim(),
    bearer_token: String(data.get("bearer_token") ?? ""),
    headers_json: String(data.get("headers_json") ?? "").trim(),
    enabled: true,
  });
}

const styles = css`
  .account-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .account {
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 12px 14px;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
  }

  .meta,
  .muted {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  form {
    display: grid;
    gap: 14px;
  }

  .grid {
    display: grid;
    gap: 14px;
    grid-template-columns: 1fr 1fr;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    font-weight: 500;
    gap: 6px;
  }

  label.full {
    grid-column: 1 / -1;
  }

  input,
  textarea {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    outline: none;
    padding: 8px 10px;
    width: 100%;
  }

  input {
    height: 36px;
  }

  textarea {
    min-height: 84px;
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  .actions app-button,
  .account app-button {
    width: auto;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }

  @media (max-width: 767px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
`;

export class AppSettingsMcp extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsMcp)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #servers;
  #loaded;
  #error;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setServers(value) {
    if (Object.is(this.#servers, value)) return;
    this.#servers = value;
    this.#updateBindings('servers');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void listMcpServers()
        .then((items) => {
        this.#setServers(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to load MCP servers.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#servers = [];
    this.#loaded = false;
    this.#error = "";
  }

  #render() {
    const serverViews = this.#servers.map(serverView);
    return `<style>${this.#escText(styles)}</style><app-settings-section title="MCP servers" description="Add remote HTTP MCP servers for your agents. Tools from these servers load alongside the global MCP servers."><!--argon-text:0-->${(serverViews.length > 0) ? `<div class="account-list">${serverViews.map((server, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${server.name}</div><div class="meta">${server.meta}</div></div><app-button variant="outline" size="sm" data-argon="3878">Remove</app-button></div>`, "initial:1:3878:" + __argonIndex, { 3878: () => {
    if (!window.confirm("Remove this MCP server from S.T.R.I.D.E.?"))
        return;
    void deleteMcpServer(server.id)
        .then(() => listMcpServers())
        .then((items) => {
        this.#setServers(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove MCP server.");
    });
} }, ["3878"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No custom MCP servers yet." : "Loading servers...")}</p>`}<!--/argon-text:0--></app-settings-section><app-settings-section title="Add MCP server" description="Only Streamable HTTP MCP servers are supported here. Authorization values are stored but not shown again."><form data-argon="4932"><div class="grid"><label>Name<input name="name" required placeholder="deepwiki" autocomplete="off" pattern="[A-Za-z][A-Za-z0-9_]{1,47}"></label><label>URL<input name="url" type="url" required placeholder="https://mcp.example.com/mcp" autocomplete="off"></label></div><label class="full">Bearer token<input name="bearer_token" type="password" autocomplete="new-password"></label><label class="full">Headers JSON<textarea name="headers_json" placeholder="{&quot;X-Tenant&quot;:&quot;acme&quot;}"></textarea></label><div class="actions"><app-button type="submit">Add server</app-button></div><p class="error"><!--argon-text:1-->${this.#escText(this.#error)}<!--/argon-text:1--></p></form></app-settings-section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const serverViews = this.#servers.map(serverView);
    if (prop === undefined || prop === 'servers' || prop === 'loaded') {
      const __argonNext = `${(serverViews.length > 0) ? `<div class="account-list">${serverViews.map((server, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${server.name}</div><div class="meta">${server.meta}</div></div><app-button variant="outline" size="sm" data-argon="3878">Remove</app-button></div>`, "0:1:3878:" + __argonIndex, { 3878: () => {
    if (!window.confirm("Remove this MCP server from S.T.R.I.D.E.?"))
        return;
    void deleteMcpServer(server.id)
        .then(() => listMcpServers())
        .then((items) => {
        this.#setServers(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove MCP server.");
    });
} }, ["3878"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No custom MCP servers yet." : "Loading servers...")}</p>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '4932').forEach(el => this.#listen(el, '4932', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitMcp(form)
        .then(() => {
        form.reset();
        return listMcpServers();
    })
        .then((items) => {
        this.#setServers(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to add MCP server.");
    });
}));
    this.#eventTargets(scope, '3878').forEach(el => this.#listen(el, '3878', 'click', () => {
    if (!window.confirm("Remove this MCP server from S.T.R.I.D.E.?"))
        return;
    void deleteMcpServer(server.id)
        .then(() => listMcpServers())
        .then((items) => {
        this.#setServers(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove MCP server.");
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
